import { useState, useEffect, useRef } from 'react'
import { useTranslation } from '../../i18n/LanguageContext'
import type { Difficulty, SearchMode, WordSearchState } from './useWordSearch'
import { useTimer } from '../useTimer'
import { s } from './wordSearchStyles'
import GamePlayHeader from '../GamePlayHeader'

const msSince = (ts: number) => Date.now() - ts

const FOUND_COLORS = [
  '#52B788', '#D4A017', '#E07A5F', '#9B5DE5', '#00BBF9',
  '#F15BB5', '#FB5607', '#3A86FF', '#8338EC', '#FF006E',
  '#00C49F', '#FFC857',
]

function getCellSize(gridSize: number): number {
  if (gridSize <= 8)  return 44
  if (gridSize <= 12) return 34
  return 26
}

const GAP = 3

interface PlayScreenProps {
  state:         WordSearchState
  difficulty:    Difficulty
  mode:          SearchMode
  onTap:         (row: number, col: number) => void
  onBeginDrag:   (row: number, col: number) => void
  onExtendDrag:  (row: number, col: number) => void
  onCommitDrag:  () => void
  onBack:        () => void
}

export function PlayScreen({ state, mode, onTap, onBeginDrag, onExtendDrag, onCommitDrag, onBack }: PlayScreenProps) {
  const { t }  = useTranslation()
  const ws     = t.wordsearch
  const timer  = useTimer(state.startTime, state.endTime)
  const { grid, selection, foundWords } = state
  const total    = grid.placedWords.length
  const found    = foundWords.length
  const progress = total > 0 ? (found / total) * 100 : 0

  // Measure available play area to compute cell sizes and orientation
  const bodyRef = useRef<HTMLDivElement>(null)
  const [bodyW, setBodyW] = useState(0)
  const [bodyH, setBodyH] = useState(0)
  useEffect(() => {
    const el = bodyRef.current
    if (!el) return
    const ro = new ResizeObserver(([entry]) => {
      setBodyW(Math.floor(entry.contentRect.width))
      setBodyH(Math.floor(entry.contentRect.height))
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const isPortrait = bodyW === 0 || bodyH > bodyW

  // Compute the largest square cell size that fits in the available area
  const WORD_PANEL_W = 168   // landscape left panel width (px)
  const WORD_PILLS_H = 60    // portrait word-pills row height (px)
  const INSTR_H      = 40    // instruction row height (px)
  const PAD          = 16    // padding around the grid (px)
  const totalGap     = GAP * (grid.size - 1)

  let cellSize: number
  if (bodyW === 0 || bodyH === 0) {
    cellSize = getCellSize(grid.size)  // initial fallback before measurement
  } else if (isPortrait) {
    const availW = bodyW - PAD * 2
    const availH = bodyH - WORD_PILLS_H - INSTR_H - PAD * 2
    const byW    = Math.floor((availW - totalGap) / grid.size)
    const byH    = Math.floor((availH - totalGap) / grid.size)
    cellSize     = Math.max(16, Math.min(52, Math.min(byW, byH)))
  } else {
    const availW = bodyW - WORD_PANEL_W - PAD * 3
    const availH = bodyH - INSTR_H - PAD * 2
    const byW    = Math.floor((availW - totalGap) / grid.size)
    const byH    = Math.floor((availH - totalGap) / grid.size)
    cellSize     = Math.max(16, Math.min(52, Math.min(byW, byH)))
  }

  // ── Drag-to-select ────────────────────────────────────────────
  const gridRef        = useRef<HTMLDivElement>(null)
  const isDragging     = useRef(false)
  const hasMoved       = useRef(false)
  const dragOrigin     = useRef<{ r: number; c: number } | null>(null)
  // Prevents ghost mouse events fired by the browser ~300ms after a touch interaction
  const lastTouchTime  = useRef(0)

  useEffect(() => {
    // Window mouseup — commit even when the pointer is released outside the grid
    const onWindowMouseUp = () => {
      if (!isDragging.current) return
      isDragging.current = false
      if (hasMoved.current) onCommitDrag()
      hasMoved.current = false
      dragOrigin.current = null
    }

    // Non-passive touchmove — prevents page scroll while dragging over the grid
    const onTouchMove = (e: TouchEvent) => {
      if (!isDragging.current) return
      e.preventDefault()
      const touch = e.touches[0]
      const el = document.elementFromPoint(touch.clientX, touch.clientY)
      const rowStr = el?.getAttribute('data-row')
      const colStr = el?.getAttribute('data-col')
      if (rowStr == null || colStr == null) return
      const r = parseInt(rowStr, 10)
      const c = parseInt(colStr, 10)
      const origin = dragOrigin.current
      if (origin && !hasMoved.current && (r !== origin.r || c !== origin.c)) {
        hasMoved.current = true
        onBeginDrag(origin.r, origin.c)
      }
      if (hasMoved.current) onExtendDrag(r, c)
    }

    window.addEventListener('mouseup', onWindowMouseUp)
    gridRef.current?.addEventListener('touchmove', onTouchMove, { passive: false })
    const gridEl = gridRef.current
    return () => {
      window.removeEventListener('mouseup', onWindowMouseUp)
      gridEl?.removeEventListener('touchmove', onTouchMove)
    }
  }, [onBeginDrag, onExtendDrag, onCommitDrag])

  const handleMouseDown = (r: number, c: number) => {
    if (msSince(lastTouchTime.current) < 500) return
    isDragging.current = true
    hasMoved.current   = false
    dragOrigin.current = { r, c }
  }

  const handleMouseEnter = (r: number, c: number) => {
    if (!isDragging.current) return
    const origin = dragOrigin.current
    if (origin && !hasMoved.current && (r !== origin.r || c !== origin.c)) {
      hasMoved.current = true
      onBeginDrag(origin.r, origin.c)
    }
    if (hasMoved.current) onExtendDrag(r, c)
  }

  const handleMouseUp = (r: number, c: number) => {
    if (!isDragging.current) return
    isDragging.current = false
    if (hasMoved.current) {
      onCommitDrag()
    } else {
      onTap(r, c)
    }
    hasMoved.current   = false
    dragOrigin.current = null
  }

  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0]
    const el = document.elementFromPoint(touch.clientX, touch.clientY)
    const rowStr = el?.getAttribute('data-row')
    const colStr = el?.getAttribute('data-col')
    if (rowStr == null || colStr == null) return
    isDragging.current = true
    hasMoved.current   = false
    dragOrigin.current = { r: parseInt(rowStr, 10), c: parseInt(colStr, 10) }
  }

  const handleTouchEnd = () => {
    lastTouchTime.current = Date.now()
    if (!isDragging.current) return
    isDragging.current = false
    if (hasMoved.current) {
      onCommitDrag()
    } else if (dragOrigin.current) {
      onTap(dragOrigin.current.r, dragOrigin.current.c)
    }
    hasMoved.current   = false
    dragOrigin.current = null
  }

  // Build highlight map: "row-col" → CSS colour
  const highlight = new Map<string, string>()
  foundWords.forEach((fw, i) => {
    const col = FOUND_COLORS[i % FOUND_COLORS.length]
    fw.cells.forEach(c => highlight.set(`${c.row}-${c.col}`, col))
  })
  selection.forEach(c => highlight.set(`${c.row}-${c.col}`, 'rgba(212,160,23,0.5)'))

  const instruction = selection.length === 0 ? ws.instructionStart : ws.instructionContinue

  const gridEl = (
    <div
      ref={gridRef}
      style={{
        ...s.grid,
        gridTemplateColumns: `repeat(${grid.size}, ${cellSize}px)`,
        gridTemplateRows:    `repeat(${grid.size}, ${cellSize}px)`,
        gap:                 GAP,
      }}
      role="grid"
      aria-label={ws.title}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {grid.cells.map((row, r) =>
        row.map((letter, c) => {
          const key        = `${r}-${c}`
          const bg         = highlight.get(key)
          const isSelected = selection.some(sc => sc.row === r && sc.col === c)
          const isLastSel  = selection.length > 0 &&
            selection[selection.length - 1].row === r &&
            selection[selection.length - 1].col === c

          return (
            <button
              key={key}
              data-row={r}
              data-col={c}
              onMouseDown={(e) => { e.preventDefault(); handleMouseDown(r, c) }}
              onMouseEnter={() => handleMouseEnter(r, c)}
              onMouseUp={() => handleMouseUp(r, c)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onTap(r, c) }
              }}
              style={{
                ...s.cell,
                width:      cellSize,
                height:     cellSize,
                fontSize:   Math.max(9, cellSize - 16),
                background: bg ?? '#FFFFFF',
                color:      bg ? '#FFFFFF' : '#2E1F14',
                border:     isLastSel ? '2px solid #D4A017' : '1px solid #E8DED0',
                boxShadow:  isLastSel ? '0 0 0 3px rgba(212,160,23,0.35)' : undefined,
                transform:  isLastSel ? 'scale(1.12)' : undefined,
                zIndex:     isLastSel ? 1 : undefined,
              }}
              aria-label={letter}
              aria-pressed={isSelected || highlight.has(key)}
            >
              {letter}
            </button>
          )
        })
      )}
    </div>
  )

  const wordListEl = (portrait: boolean) =>
    grid.placedWords.map((pw, i) => {
      const fw    = foundWords.find(f => f.normalized === pw.normalized)
      const color = fw ? FOUND_COLORS[foundWords.indexOf(fw) % FOUND_COLORS.length] : undefined
      return portrait ? (
        <span
          key={pw.normalized}
          style={{
            ...s.wordPill,
            background:    color ?? '#F5F0E8',
            color:         color ? '#FFFFFF' : '#3D2B1F',
            textDecoration: fw ? 'line-through' : 'none',
            opacity:       fw ? 0.7 : 1,
          }}
        >
          {fw ? '✓ ' : ''}{pw.word}
        </span>
      ) : (
        <div
          key={pw.normalized}
          style={{
            ...s.wordItem,
            color:          color ?? '#1B4332',
            textDecoration: fw ? 'line-through' : 'none',
            opacity:        fw ? 0.6 : 1,
            borderLeft:     color ? `4px solid ${color}` : '4px solid transparent',
            animationDelay: `${i * 0.04}s`,
          }}
        >
          {fw ? '✓ ' : ''}{pw.word}
        </div>
      )
    })

  return (
    <div style={s.playRoot}>
      <GamePlayHeader
        title={ws.title}
        subtitle={ws.modes[mode].label}
        stats={[
          { value: `${found}/${total}`, label: ws.wordsFound },
          { value: timer,               label: ws.time },
        ]}
        progress={progress}
        progressNow={found}
        progressMax={total}
        onBack={onBack}
        backLabel={ws.backToDashboard}
      />

      {/* ── Play body — measured container ── */}
      <div ref={bodyRef} style={{ ...s.playBody, flexDirection: isPortrait ? 'column' : 'row' }}>
        {isPortrait ? (
          /* Portrait: word pills on top, grid centred below */
          <>
            <div style={s.wordPillsRow}>{wordListEl(true)}</div>
            <div style={s.gridCenter}>{gridEl}</div>
            <div style={s.instructionRow}>
              <span style={s.instruction}>{instruction}</span>
            </div>
          </>
        ) : (
          /* Landscape: word list on left, grid centred on right */
          <>
            <div style={s.wordPanel}>
              <div style={s.wordList}>{wordListEl(false)}</div>
            </div>
            <div style={s.landscapeRight}>
              <div style={s.gridCenter}>{gridEl}</div>
              <div style={s.instructionRow}>
                <span style={s.instruction}>{instruction}</span>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
